import React, { FC, useState, useEffect } from 'react';
import { Card, Typography, Tabs, Button, Upload, notification, Space, Radio, Empty, Spin, Select } from 'antd';
import { UploadOutlined, CodeOutlined, TableOutlined, BarChartOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTernaryDarkMode } from 'usehooks-ts';
import { useTransactionsStore, TransactionData } from '@/store/transactions';
import type { UploadProps } from 'antd';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

interface PayloadRendererProps {
  configId: string;
}

const PayloadRenderer: FC<PayloadRendererProps> = ({ configId }) => {
  const { isDarkMode } = useTernaryDarkMode();
  const [payload, setPayload] = useState<any>(null);
  const [viewMode, setViewMode] = useState<'json' | 'table' | 'chart'>('json');
  const [selectedTransaction, setSelectedTransaction] = useState<string>('latest');
  const [loading, setLoading] = useState(false);
  
  // Get transactions from store
  const { transactions, fetchTransactions, isLoading } = useTransactionsStore();
  const transactionData = transactions[configId]?.data || [];
  
  // Load real transaction payload on component mount
  useEffect(() => {
    const loadRealPayload = async () => {
      if (transactionData.length === 0) {
        setLoading(true);
        try {
          await fetchTransactions(configId);
        } catch (error) {
          console.error('Failed to fetch transactions:', error);
        } finally {
          setLoading(false);
        }
      }
    };
    
    loadRealPayload();
  }, [configId, fetchTransactions, transactionData.length]);
  
  // Auto-select latest transaction payload when data is available
  useEffect(() => {
    if (transactionData.length > 0 && selectedTransaction === 'latest') {
      const latestTransaction = transactionData[0]; // Assuming sorted by date
      if (latestTransaction?.payload) {
        setPayload(latestTransaction.payload);
      }
    }
  }, [transactionData, selectedTransaction]);
  
  // Try to load payload from local storage on component mount (fallback)
  useEffect(() => {
    if (!payload) {
      try {
        const savedPayload = localStorage.getItem(`payload_${configId}`);
        if (savedPayload) {
          setPayload(JSON.parse(savedPayload));
        }
      } catch (error) {
        console.error('Failed to load saved payload:', error);
      }
    }
  }, [configId, payload]);
  
  // Save payload to local storage when it changes
  useEffect(() => {
    if (payload) {
      try {
        localStorage.setItem(`payload_${configId}`, JSON.stringify(payload));
      } catch (error) {
        console.error('Failed to save payload:', error);
      }
    }
  }, [payload, configId]);

  const handleTransactionSelect = (transactionId: string) => {
    setSelectedTransaction(transactionId);
    if (transactionId === 'latest' && transactionData.length > 0) {
      const latestTransaction = transactionData[0];
      setPayload(latestTransaction?.payload || null);
    } else if (transactionId !== 'latest') {
      const selectedTx = transactionData.find((tx: TransactionData) => tx._id === transactionId);
      setPayload(selectedTx?.payload || null);
    }
  };

  const handleRefreshData = async () => {
    setLoading(true);
    try {
      await fetchTransactions(configId, undefined, undefined, undefined, undefined, true);
      if (selectedTransaction === 'latest' && transactionData.length > 0) {
        setPayload(transactionData[0]?.payload || null);
      }
    } catch (error) {
      console.error('Failed to refresh transactions:', error);
      notification.error({
        message: 'Refresh Failed',
        description: 'Failed to refresh transaction data',
      });
    } finally {
      setLoading(false);
    }
  };
  
  const uploadProps: UploadProps = {
    name: 'file',
    action: '',
    beforeUpload: (file) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          if (e.target?.result) {
            const content = e.target.result.toString();
            const parsedData = JSON.parse(content);
            setPayload(parsedData);
            setSelectedTransaction('custom');
            notification.success({
              message: 'Upload Successful',
              description: 'JSON payload loaded successfully',
            });
          }
        } catch (error) {
          console.error('Failed to parse JSON file:', error);
          notification.error({
            message: 'Upload Failed',
            description: 'Please upload a valid JSON file',
          });
        }
      };
      
      reader.readAsText(file);
      return false; // Prevent actual upload
    },
    showUploadList: false,
  };
  
  const handlePasteJson = () => {
    try {
      // Get clipboard data - this is a mock implementation
      const clipboardData = prompt('Paste your JSON data here:');
      
      if (!clipboardData) {
        return; // User canceled
      }
      
      try {
        const parsedData = JSON.parse(clipboardData);
        setPayload(parsedData);
        setSelectedTransaction('custom');
        notification.success({
          message: 'Paste Successful',
          description: 'JSON payload loaded successfully',
        });
      } catch (parseError) {
        notification.error({
          message: 'Parse Error',
          description: 'The data you pasted is not valid JSON',
        });
      }
    } catch (error) {
      console.error('Failed to parse pasted JSON:', error);
      notification.error({
        message: 'Paste Failed',
        description: 'Please paste valid JSON data',
      });
    }
  };
  
  const samplePayloads = [
    {
      name: 'Sample Order',
      data: {
        orderId: '12345',
        customer: {
          id: 'CUST001',
          name: 'John Doe',
          email: 'john@example.com'
        },
        items: [
          { id: 'ITEM001', name: 'Product A', quantity: 2, price: 25.99 },
          { id: 'ITEM002', name: 'Product B', quantity: 1, price: 59.99 }
        ],
        total: 111.97,
        status: 'pending',
        createdAt: '2023-01-15T12:30:45Z'
      }
    },
    {
      name: 'Sample Customer',
      data: {
        id: 'CUST001',
        name: 'Jane Smith',
        email: 'jane@example.com',
        address: {
          street: '123 Main St',
          city: 'Anytown',
          state: 'CA',
          zipCode: '90210'
        },
        phone: '+1-555-123-4567',
        registeredDate: '2022-05-10T09:15:23Z',
        preferences: {
          newsletter: true,
          marketing: false
        }
      }
    }
  ];
  
  const loadSamplePayload = (sample: any) => {
    setPayload(sample.data);
    setSelectedTransaction('sample');
    notification.info({
      message: 'Sample Loaded',
      description: `Loaded sample: ${sample.name}`,
    });
  };
  
  const renderContent = () => {
    if (loading || isLoading) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <Spin size="large" tip="Loading payload data..." />
        </div>
      );
    }
    
    if (!payload) {
      return (
        <Empty 
          description="No payload data loaded" 
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      );
    }
    
    switch (viewMode) {
      case 'json':
        return (
          <div className="json-view-container">
            <pre>
              {JSON.stringify(payload, null, 2)}
            </pre>
          </div>
        );
      
      case 'table':
        return (
          <div className="table-view-container">
            <Paragraph>Table view is coming soon</Paragraph>
          </div>
        );
      
      case 'chart':
        return (
          <div className="chart-view-container">
            <Paragraph>Chart view is coming soon</Paragraph>
          </div>
        );
      
      default:
        return null;
    }
  };
  
  return (
    <div className={`payload-renderer ${isDarkMode ? 'dark' : 'light'}`}>
      <Card className="renderer-card">
        <Title level={4}>Payload Renderer</Title>
        <Text type="secondary">
          Visualize and explore JSON payloads from your integration
        </Text>
        
        <div className="controls">
          <Space direction="vertical" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Text strong>Data Source:</Text>
              <Button 
                icon={<ReloadOutlined />} 
                onClick={handleRefreshData}
                loading={loading || isLoading}
                size="small"
              >
                Refresh
              </Button>
            </div>
            
            <Select
              value={selectedTransaction}
              onChange={handleTransactionSelect}
              style={{ width: '100%' }}
              placeholder="Select transaction payload"
            >
              <Option value="latest">Latest Transaction</Option>
              {transactionData.slice(0, 10).map((tx: any) => (
                <Option key={tx._id} value={tx._id}>
                  {new Date(tx.startDate || tx.timestamp).toLocaleString()} - {tx.status}
                </Option>
              ))}
              <Option value="custom">Custom Upload</Option>
              <Option value="sample">Sample Data</Option>
            </Select>
            
            {transactionData.length > 0 && (
              <Text type="secondary">
                {transactionData.length} transactions available
              </Text>
            )}
          </Space>
        </div>
        
        {selectedTransaction === 'custom' && (
          <div className="controls">
            <Space>
              <Upload {...uploadProps}>
                <Button icon={<UploadOutlined />}>Upload JSON File</Button>
              </Upload>
              <Button onClick={handlePasteJson} icon={<CodeOutlined />}>
                Paste JSON
              </Button>
            </Space>
          </div>
        )}
        
        {selectedTransaction === 'sample' && (
          <div className="sample-selector">
            <Text strong>Sample Payloads:</Text>
            <Space style={{ marginTop: 8 }}>
              {samplePayloads.map((sample, index) => (
                <Button
                  key={index}
                  onClick={() => loadSamplePayload(sample)}
                  size="small"
                >
                  {sample.name}
                </Button>
              ))}
            </Space>
          </div>
        )}
        
        <div className="view-mode-selector">
          <Radio.Group
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            buttonStyle="solid"
          >
            <Radio.Button value="json">
              <CodeOutlined /> JSON
            </Radio.Button>
            <Radio.Button value="table">
              <TableOutlined /> Table
            </Radio.Button>
            <Radio.Button value="chart">
              <BarChartOutlined /> Chart
            </Radio.Button>
          </Radio.Group>
        </div>
        
        <div className="renderer-content">
          {renderContent()}
        </div>
      </Card>
    </div>
  );
};

export default PayloadRenderer; 